import { useEffect, useState } from "react";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const ACTION_STYLE = { création: "border-emerald-600 text-emerald-700", modification: "bg-blue-600 text-white", annulation: "bg-[#059669] text-white", suppression: "bg-destructive text-white", restauration: "bg-emerald-600 text-white" };
const DOC_LABEL = { BE: "Bon d'entrée", BS: "Bon de sortie", BT: "Bon de transfert", VENTE: "Vente", MOUVEMENT: "Mouvement" };

const Details = ({ d }) => {
  if (!d || !Object.keys(d).length) return null;
  return <div className="text-xs text-muted-foreground">{Object.entries(d).map(([k, v]) => <div key={k}><b>{k}</b> : {Array.isArray(v) ? v.map((x) => typeof x === "object" ? Object.values(x).join(" ") : x).join(" ; ") : String(v)}</div>)}</div>;
};

export default function Audit() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [trashOnly, setTrashOnly] = useState(false);
  const load = () => api.get("/audit").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);
  const restore = async (a) => {
    if (!window.confirm(`Restaurer ${DOC_LABEL[a.doc_type] || a.doc_type} ${a.number} ? Le stock sera recalculé.`)) return;
    try { await api.post(`/audit/${a.id}/restore`); toast.success(`${a.number} restauré`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const list = items.filter((a) => (!trashOnly || a.restorable) && `${a.number} ${a.user_name} ${a.action} ${a.reason} ${a.doc_type}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="audit-page">
      <PageHeader title="Journal des modifications" subtitle="Qui a créé, modifié, annulé ou supprimé chaque mouvement de stock">
        <Button variant={trashOnly ? "default" : "outline"} onClick={() => setTrashOnly(!trashOnly)} data-testid="audit-trash-toggle"><RotateCcw size={16} /> Corbeille ({items.filter((a) => a.restorable).length})</Button>
        <Input placeholder="Rechercher (n°, utilisateur, motif…)" value={q} onChange={(e) => setQ(e.target.value)} className="w-72" data-testid="audit-search" />
      </PageHeader>
      <div className="rise overflow-hidden rounded-lg border bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Action</TableHead><TableHead>Document</TableHead><TableHead>N°</TableHead><TableHead>Par</TableHead><TableHead>Motif</TableHead><TableHead>Détails</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody data-testid="audit-table">
            {list.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Aucune entrée.</TableCell></TableRow>}
            {list.map((a) => <TableRow key={a.id} data-testid={`audit-row-${a.id}`}><TableCell className="text-xs">{fmtDate(a.created_at)}</TableCell><TableCell><Badge variant="outline" className={ACTION_STYLE[a.action] || ""}>{a.action}</Badge>{a.restored && <span className="block text-xs text-emerald-700">restauré</span>}</TableCell><TableCell className="text-xs">{DOC_LABEL[a.doc_type] || a.doc_type}</TableCell><TableCell className="font-mono text-xs font-semibold">{a.number}</TableCell><TableCell className="text-sm">{a.user_name}</TableCell><TableCell className="text-sm">{a.reason || "—"}</TableCell><TableCell><Details d={a.details} /></TableCell>
              <TableCell>{user.role === "admin" && a.restorable && <Button size="sm" variant="outline" className="border-emerald-600 text-emerald-700" onClick={() => restore(a)} data-testid={`audit-restore-${a.id}`}><RotateCcw size={14} /> Restaurer</Button>}</TableCell></TableRow>)}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
